import { useEffect, useState } from 'react';
import { get } from '../api';
import type { Cubo, Excepcion, Movimiento, Propuesta, ArticuloPlan } from '../tipos';
import { GraficoPlan, etiquetaFecha, formatear } from '../components/GraficoPlan';
import { Accion, Dist, Gravedad, Vacio } from '../components/ui';

interface Respuesta {
  items: Array<{ item_code: string; warehouse: string; item: ArticuloPlan; buckets: Cubo[] }>;
  demands: Movimiento[];
  supplies: Movimiento[];
  recommendations: Propuesta[];
  exceptions: Excepcion[];
}

const ORIGEN: Record<string, string> = {
  sales: 'Pedido de venta',
  production: 'Orden de fabricación',
  forecast: 'Previsión',
  dependent: 'Componente de una propuesta',
  safety: 'Stock de seguridad',
  committed: 'Comprometido en SAP',
  purchase: 'Pedido de compra',
  transfer: 'Traslado',
  onhand: 'Stock',
};

/**
 * Todo lo que hay que saber de un artículo, en una pantalla.
 *
 * El gráfico enseña la forma; las dos tablas de debajo enseñan de dónde sale
 * cada número. Sin ellas, el gráfico sería una opinión: la pregunta que se
 * hace siempre quien mira un MRP es «¿y esto por qué?», y la respuesta tiene
 * que estar aquí y no en SAP.
 */
export function DetalleArticulo({
  runId,
  codigo,
  almacen,
  onCerrar,
}: {
  runId: string;
  codigo: string;
  almacen: string;
  onCerrar: () => void;
}): JSX.Element {
  const [datos, setDatos] = useState<Respuesta | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vigente = true;
    setDatos(null);
    void get<Respuesta>(`/runs/${runId}/items/${encodeURIComponent(codigo)}?warehouse=${encodeURIComponent(almacen)}`)
      .then((r) => vigente && setDatos(r))
      .catch((e: unknown) => vigente && setError(String(e)));
    return () => {
      vigente = false;
    };
  }, [runId, codigo, almacen]);

  if (error) return <div className="aviso-caja error">{error}</div>;
  if (!datos) return <Vacio>Cargando…</Vacio>;

  const fila = datos.items[0];
  if (!fila) return <Vacio>Este artículo no está en el plan.</Vacio>;
  const art = fila.item;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <button className="secundario" onClick={onCerrar}>← Volver</button>
        <h1>{art.itemCode}</h1>
        <span className="sutil">{art.name}</span>
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', flexWrap: 'wrap' }}>
          {art.excluded ? <Dist tono="neutro" icono="—">Fuera del MRP</Dist> : null}
          {art.procurement === 'make' ? <Dist tono="neutro" icono="⚙">Se fabrica</Dist> : <Dist tono="neutro" icono="↓">Se compra</Dist>}
          {art.procurement === 'make' && !art.hasBom ? <Dist tono="critico" icono="■">Sin escandallo</Dist> : null}
          {art.procurement === 'buy' && !art.preferredVendor ? <Dist tono="aviso" icono="▲">Sin proveedor</Dist> : null}
        </div>
      </div>

      <div className="rejilla cifras" style={{ marginBottom: 14 }}>
        <Dato etiqueta="Stock ahora" valor={formatear(art.onHand)} pie={art.unit ?? ''} />
        <Dato etiqueta="Mínimo" valor={formatear(art.safetyStock)} />
        <Dato etiqueta="Plazo" valor={`${art.leadTimeDays} d`} pie={art.leadTimeDays === 0 ? 'no está en SAP' : ''} />
        <Dato etiqueta="Lote mínimo" valor={formatear(art.minOrderQty)} pie={art.orderMultiple > 0 ? `múltiplo de ${formatear(art.orderMultiple)}` : ''} />
        <Dato etiqueta="Nivel" valor={String(art.lowLevel)} pie={art.lowLevel === 0 ? 'producto final' : 'componente'} />
      </div>

      <div className="tarjeta" style={{ marginBottom: 14 }}>
        <h2 style={{ marginBottom: 10 }}>Cómo evoluciona el stock</h2>
        <GraficoPlan buckets={fila.buckets} safetyStock={art.safetyStock} unidad={art.unit} />
      </div>

      {datos.recommendations.length > 0 && (
        <div className="tarjeta" style={{ marginBottom: 14 }}>
          <h2 style={{ marginBottom: 10 }}>Qué propone el MRP</h2>
          <table>
            <thead>
              <tr>
                <th>Acción</th>
                <th className="num">Cantidad</th>
                <th>Necesita</th>
                <th>Lanzar</th>
                <th>Para cubrir</th>
              </tr>
            </thead>
            <tbody>
              {datos.recommendations.map((r) => (
                <tr key={r.id}>
                  <td>
                    <Accion accion={r.action} />
                    {r.note ? <div className="sutil">{r.note}</div> : null}
                  </td>
                  <td className="num mono">{formatear(r.quantity)}</td>
                  <td className="mono">{etiquetaFecha(r.due_date)}</td>
                  <td className="mono">
                    {etiquetaFecha(r.release_date)}
                    {r.late_days > 0 ? <div><Dist tono="critico" icono="■">{r.late_days} d tarde</Dist></div> : null}
                  </td>
                  <td className="sutil">
                    {r.pegging.map((p, i) => (
                      <div key={i}>{formatear(p.quantity)} · {p.label}</div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="rejilla dos">
        <Movimientos titulo="De dónde sale la necesidad" filas={datos.demands} signo="−" />
        <Movimientos titulo="Qué cobertura hay ya" filas={datos.supplies} signo="+" />
      </div>

      {datos.exceptions.length > 0 && (
        <div className="tarjeta" style={{ marginTop: 14 }}>
          <h2 style={{ marginBottom: 10 }}>Incidencias de este artículo</h2>
          {datos.exceptions.map((e) => (
            <div key={e.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8 }}>
              <Gravedad severity={e.severity} />
              <span>{e.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* La tabla del gráfico. No es un extra: tres de los colores de la
          paleta quedan por debajo de 3:1 sobre fondo claro, y la regla es que
          entonces haya etiquetas visibles o una tabla. Además es lo que se
          copia a un correo. */}
      <details className="tarjeta" style={{ marginTop: 14 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Ver el gráfico como tabla</summary>
        <table style={{ marginTop: 10 }}>
          <thead>
            <tr>
              <th>Periodo</th>
              <th className="num">Necesidades</th>
              <th className="num">Cobertura</th>
              <th className="num">Propuesto</th>
              <th className="num">Proyectado</th>
              <th className="num">Si no se hace nada</th>
            </tr>
          </thead>
          <tbody>
            {fila.buckets.map((b) => (
              <tr key={b.from}>
                <td className="mono">{etiquetaFecha(b.from)} – {etiquetaFecha(b.to)}</td>
                <td className="num mono">{formatear(b.demand)}</td>
                <td className="num mono">{formatear(b.supply)}</td>
                <td className="num mono">{formatear(b.planned)}</td>
                <td className="num mono">{formatear(b.projected)}</td>
                <td className="num mono">{formatear(b.projectedBare)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

function Dato({ etiqueta, valor, pie }: { etiqueta: string; valor: string; pie?: string }): JSX.Element {
  return (
    <div className="tarjeta cifra">
      <div className="etiqueta">{etiqueta}</div>
      <div className="valor" style={{ fontSize: 22 }}>{valor}</div>
      {pie ? <div className="pie">{pie}</div> : null}
    </div>
  );
}

function Movimientos({ titulo, filas, signo }: { titulo: string; filas: Movimiento[]; signo: string }): JSX.Element {
  return (
    <div className="tarjeta" style={{ padding: 0 }}>
      <h2 style={{ padding: '14px 16px 8px' }}>{titulo}</h2>
      {filas.length === 0 ? (
        <Vacio>Nada en el horizonte.</Vacio>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Fecha</th>
              <th className="num">Cantidad</th>
              <th>Origen</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((m) => (
              <tr key={m.id}>
                <td className="mono">{etiquetaFecha(m.due_date)}</td>
                <td className="num mono">{signo}{formatear(m.quantity)}</td>
                <td>
                  <div>{m.label}</div>
                  <div className="sutil">{ORIGEN[m.kind] ?? m.kind}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
